// src/pages/SettingsPage.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Palette, Bell, Shield, LogOut, Sun, Moon } from 'lucide-react';
import { Card, Btn, ConfirmModal } from '../components/ui';
import userService from '../services/userService';

const SettingsPage = ({ user, onLogout, dark, toggleDark, toast }) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    const r = await userService.deleteAccount(user.uid);
    if (r.success) {
      setShowDeleteConfirm(false);
      toast(t('profile.toast.accountDeleted'), 'info');
      onLogout();
    } else {
      toast(r.error, 'error');
    }
  };

  return (
    <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4 max-w-2xl">
      <Helmet>
        <title>{t('settings.title')} — HmongHearts</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <h1 className="text-lg sm:text-2xl font-bold mb-6 dark:text-gray-100">
        {t('settings.title')}
      </h1>

      {/* Apparence */}
      <Card className="p-4 mb-4" data-testid="settings-appearance">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Palette size={18} className="text-violet-600 dark:text-violet-400" />
          </div>
          <h2 className="text-base font-medium dark:text-gray-100">{t('settings.appearance')}</h2>
        </div>
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
          <div className="flex items-center gap-3">
            {dark ? (
              <Moon size={18} className="text-gray-500 dark:text-gray-400" />
            ) : (
              <Sun size={18} className="text-gray-500 dark:text-gray-400" />
            )}
            <div>
              <p className="text-sm font-medium dark:text-gray-200 leading-tight">
                {t('settings.darkMode')}
              </p>
              <p className="text-xs text-gray-400">{t('settings.darkModeDesc')}</p>
            </div>
          </div>
          <button
            onClick={toggleDark}
            data-testid="settings-btn-darkmode"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${dark ? 'bg-rose-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            aria-label={t('settings.darkMode')}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${dark ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>
      </Card>

      {/* Notifications */}
      <Card className="p-4 mb-4" data-testid="settings-notifications">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Bell size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-medium dark:text-gray-100">
              {t('settings.notifications')}
            </h2>
            <span className="text-[10px] font-bold uppercase bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
              {t('settings.notificationsSoon')}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {t('settings.notificationsSoonDesc')}
        </p>
      </Card>

      {/* Confidentialité & Données */}
      <Card className="p-4 mb-4" data-testid="settings-privacy">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Shield size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-base font-medium dark:text-gray-100">{t('settings.privacy')}</h2>
        </div>
        <div className="flex flex-col items-center sm:items-start">
          <Btn
            v="outline"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-sm text-red-500 hover:text-red-600 border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700"
            data-testid="settings-btn-delete"
          >
            {t('settings.deleteAccount')}
          </Btn>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center sm:text-left">
            {t('settings.deleteAccountDesc')}
          </p>
        </div>
      </Card>

      {/* Compte */}
      <Card className="p-4" data-testid="settings-account">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <LogOut size={18} className="text-gray-500 dark:text-gray-400" />
          </div>
          <h2 className="text-base font-medium dark:text-gray-100">{t('settings.account')}</h2>
        </div>
        <Btn
          v="outline"
          onClick={onLogout}
          data-testid="settings-btn-logout"
          className="w-full sm:w-auto text-sm"
        >
          <LogOut size={16} />
          {t('settings.logout')}
        </Btn>
      </Card>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title={t('profile.deleteAccount.title')}
        message={t('profile.deleteAccount.message')}
        confirmVariant="danger"
      />
    </div>
  );
};

export default SettingsPage;
